import { mapActions, mapGetters } from "vuex";
import toast from "@/utils/toast";
import { cleanObject } from "./utils/utils";
import moment from "moment";

export default {
  name: "IdpAttendanceMain",

  components: {},
  data() {
    return {
      filters: {
        page: 1,
        pageSize: 10,
        order: "desc",
        field: "updated_at",
        search: "",
        year: String(new Date().getFullYear()),
      },
      scheduleData: null,
      activeFiltersCount: 0,
      isLoading: false,
      isModalEventLoading: false,
      isModalDetailAttendanceLoading: false,
      isSubmitAttendanceLoading: false,
      _searchTimer: null,
    };
  },

  computed: {
    ...mapGetters("idpAttendance", ["attendances", "detailEvent", "detailAttendance"]),
    ...mapGetters("users", ["profileData"]),

    yearOptions() {
      const currentYear = new Date().getFullYear();
      const range = 5;

      return Array.from({ length: range + 1 }, (_, i) => {
        const year = currentYear - i;
        return {
          id: String(year),
          label: String(year),
        };
      });
    },

    showingFrom() {
      if (!this.attendances.totalItems) return 0;
      return (this.filters.page - 1) * this.filters.pageSize + 1;
    },

    showingTo() {
      const to = this.filters.page * this.filters.pageSize;
      return to > this.attendances.totalItems ? this.attendances.totalItems : to;
    },
  },

  watch: {
    filters: {
      deep: true,
      handler(newFilters) {
        this.updateUrlQuery(newFilters);
      },
    },

    "filters.year"(val, oldVal) {
      if (val !== oldVal) {
        this.applyFilterChange();
      }
    },

    "filters.search"(val, oldVal) {
      if (val !== oldVal) {
        this.onSearch();
      }
    },

    "filters.start_date"(val, oldVal) {
      if (!val) {
        this.filters.end_date = null;
        return;
      }

      if (this.filters.end_date && moment(this.filters.end_date).isBefore(val, "day")) {
        this.filters.end_date = null;
      }

      this.filters.year = null;
      this.applyFilterChange();
    },

    "filters.end_date"(val, oldVal) {
      if (val !== oldVal) {
        this.applyFilterChange();
      }
    },

    "$route.query": {
      handler() {
        this.syncFiltersFromQuery();
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions("idpAttendance", [
      "getAttendances",
      "submitAttendance",
      "getDetailEvent",
      "getDetailAttendance",
      "resetDataAttendance",
    ]),

    formatDateForApi(date) {
      return date ? moment(date).format("YYYY-MM-DD") : null;
    },

    disableEndDate(date) {
      if (!this.filters.start_date) return true;

      const start = moment(this.filters.start_date).startOf("day");
      const current = moment(date).startOf("day");

      return current.isBefore(start);
    },

    showFilterModal() {
      this.$bvModal.show("modal-filter");
    },

    buildQueryFromFilters() {
      const raw = {
        page: this.filters.page,
        pageSize: this.filters.pageSize,
        order: this.filters.order,
        field: this.filters.field,
        search: this.filters.search,
        year: this.filters.year,
        start_date: this.formatDateForApi(this.filters.start_date),
        end_date: this.formatDateForApi(this.filters.end_date),
        all: this.filters.all,
      };

      return cleanObject(raw);
    },

    async loadData() {
      try {
        this.isLoading = true;

        const query = this.buildQueryFromFilters();

        await this.getAttendances(query);

        if (this.attendances.page && this.filters.page !== this.attendances.page) this.filters.page = this.attendances.page;
        if (this.attendances.limit && this.filters.pageSize !== this.attendances.limit) this.filters.pageSize = this.attendances.limit;
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || "Data absensi belum berhasil dimuat";
        toast({ type: "error", title: "Error", message });
      } finally {
        this.isLoading = false;
      }
    },

    handleOpenSubmitAttendanceModal(data) {
      this.scheduleData = { ...data, training_title: this.detailEvent?.training?.title };

      this.$bvModal.show("submit-attendance-modal");
    },

    applyFilterChange() {
      this.filters.page = 1;
      this.loadData();
    },

    onPageChange(payload) {
      let page = null;
      if (typeof payload === "number") page = payload;
      else if (payload?.currentPage !== undefined) page = payload.currentPage;
      else if (payload?.currentPageNumber !== undefined) page = payload.currentPageNumber;
      if (page === null) return;
      this.filters.page = page;
      this.loadData();
    },

    onPageChangeFromBv(page) {
      this.filters.page = page;
      this.loadData();
    },

    onPerPageChange(payload) {
      let per = null;
      if (typeof payload === "number") per = payload;
      else if (payload?.currentPerPage !== undefined) per = payload.currentPerPage;
      else if (payload?.perPage !== undefined) per = payload.perPage;
      if (per === null) return;
      this.filters.pageSize = per;
      this.filters.page = 1;
      this.loadData();
    },

    onSortChange(params) {
      if (!Array.isArray(params) || params.length === 0) return;
      const { field, type } = params[0] || {};
      const isReset = type === "none";
      this.filters = {
        ...this.filters,
        field: isReset ? null : field,
        order: isReset ? null : type || "asc",
        page: 1,
      };
      this.loadData();
    },

    onSearch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.filters.page = 1;
        this.loadData();
      }, 500);
    },

    clearFilter() {
      this.filters = { ...this.filters, search: "", page: 1, year: String(new Date().getFullYear()), start_date: null, end_date: null };
      this.loadData();
    },

    updateUrlQuery(filters) {
      const query = cleanObject({
        page: filters.page,
        pageSize: filters.pageSize,
        order: filters.order,
        field: filters.field,
        search: filters.search,
        year: filters.year,
        start_date: this.formatDateForApi(filters.start_date),
        end_date: this.formatDateForApi(filters.end_date),
      });

      const current = JSON.stringify(this.$route.query);
      const next = JSON.stringify(query);

      if (current !== next) {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    syncFiltersFromQuery() {
      const query = this.$route.query;
      const parsed = {
        page: Number(query.page) || 1,
        pageSize: Number(query.pageSize) || 10,
        order: query.order || null,
        field: query.field || null,
        search: query.search || "",
        year: query.year || String(new Date().getFullYear()),
        start_date: query.start_date ? moment(query.start_date, "YYYY-MM-DD").toDate() : null,
        end_date: query.end_date ? moment(query.end_date, "YYYY-MM-DD").toDate() : null,
      };

      Object.keys(parsed).forEach((key) => {
        this.$set(this.filters, key, parsed[key]);
      });
    },

    async handleViewDetailEvent(payload) {
      this.isModalEventLoading = true;

      await this.getDetailEvent({ id: payload.id });

      this.$nextTick(() => {
        this.$bvModal.show("event-modal");
      });
      this.isModalEventLoading = false;
    },

    async handleViewDetailAttendance(payload) {
      this.isModalDetailAttendanceLoading = true;

      await this.getDetailAttendance({ id: payload.id });

      this.$nextTick(() => {
        this.$bvModal.show("attendance-modal");
      });
      this.isModalDetailAttendanceLoading = false;
    },

    async handleSubmitAttendance(payload) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin mengirim absensi?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, kirim",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.isSubmitAttendanceLoading = true;

            await this.submitAttendance({ id: payload.id });

            toast({ type: "success", title: "Success", message: "Absensi berhasil dikirim" });
            this.$bvModal.hide("submit-attendance-modal");
            this.$bvModal.hide("event-modal");
          } catch (error) {
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.isSubmitAttendanceLoading = false;
            this.loadData();
          }
        }
      });
    },
  },

  async mounted() {
    this.syncFiltersFromQuery();

    await Promise.all([this.loadData()]);
  },

  beforeDestroy() {
    clearTimeout(this._searchTimer);
    this.resetDataAttendance();
  },
};
